import type { LucideIcon } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

export interface Segment<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  /** The accessible name of the whole choice: "How tall the rows are". */
  label: string;
  value: T;
  options: readonly Segment<T>[];
  onChange: (value: T) => void;
  /**
   * Show only the icons, the words moving to the tooltip and the accessible
   * name. For a setting whose icons picture its own effect (row height).
   */
  iconOnly?: boolean;
}

/**
 * Two to four ways of looking at the same rows, one of them chosen: a sunken
 * track with the choice raised out of it, the thumb sliding between segments.
 * A radio group to assistive technology, so the arrow keys move the choice
 * and Tab enters and leaves the group as one stop.
 */
export function SegmentedControl<T extends string>(
  props: SegmentedControlProps<T>,
): React.JSX.Element {
  const { value, options, onChange } = props;
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  // The thumb is measured rather than computed so segments can keep their
  // natural widths (words of different lengths) and still slide.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (track === null) return;
    const measure = (): void => {
      const chosen = track.querySelector<HTMLElement>('[aria-checked="true"]');
      setThumb(chosen === null ? null : { left: chosen.offsetLeft, width: chosen.offsetWidth });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [value, options]);

  function choose(index: number): void {
    const option = options[(index + options.length) % options.length];
    if (option === undefined) return;
    onChange(option.value);
    trackRef.current
      ?.querySelectorAll<HTMLElement>('[role="radio"]')
      [options.indexOf(option)]?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent, index: number): void {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        choose(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        choose(index - 1);
        break;
      case 'Home':
        choose(0);
        break;
      case 'End':
        choose(options.length - 1);
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  return (
    <div
      ref={trackRef}
      className={props.iconOnly === true ? 'segmented icon-only' : 'segmented'}
      role="radiogroup"
      aria-label={props.label}
    >
      {thumb !== null && (
        <span
          className="segmented-thumb"
          aria-hidden="true"
          style={{ transform: `translateX(${thumb.left}px)`, width: thumb.width }}
        />
      )}
      {options.map((option, index) => {
        const checked = option.value === value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            className="segment"
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={props.iconOnly === true ? option.label : undefined}
            title={props.iconOnly === true ? option.label : undefined}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {Icon !== undefined && <Icon aria-hidden="true" />}
            {props.iconOnly !== true && option.label}
          </button>
        );
      })}
    </div>
  );
}
